import { drawReceiptCanvas } from './eposReceiptCanvas.js'

// let ipAddress = '192.168.6.137'
const port = '8043'
let printer = null // 印表機物件
const RASTER_BAND_HEIGHT = 256 // 點陣圖分段送印的高度(dots)
const PRINT_SETUP_TIMEOUT = 10000 // 連線到送出列印的逾時(ms)，避免出單機無回應時呼叫端的 loading 一直轉
const PRINTER_TIMEOUT = 60000 // 印表機處理列印的逾時(ms)
const RELEASE_FALLBACK_DELAY = 5000 // 送出後遲遲收不到列印結果時，額外等待多久強制釋放連線(ms)

/**
 * 目前使用的明細單模板版本
 * 新流程一律使用此常數；若需退回舊版，改回 3 即可
 */
export const LATEST_RECEIPT_TEMPLATE_VERSION = 4

/**
 * 嘗試連接印表機
 */
export const testPrinterConnection = async (ipAddress, retryCount = 5) => {
  const connectPrinter = async () => {
    let result = 'fail'
    return await new Promise(async (resolve) => {
      for (let i = 0; i < retryCount; i++) {
        try {
          const res = await connectEPOSDevice(ipAddress, port)
          result = res
          if (res === 'success') break
        } catch (e) {
          console.error(`connect fail: ${e}`)
        }
      }
      resolve(result)
    })
  }

  return await connectPrinter()
}

/**
 * 連結 EPOS 印表機裝置
 * @param {*} ipAddress
 * @param {*} port
 * @returns
 */
const connectEPOSDevice = async (ipAddress, port) => {
  const ePosDevCheck = new epson.ePOSDevice()
  return await new Promise((resolve) => {
    ePosDevCheck.connect(
      ipAddress,
      port,
      (data) => {
        if (data === 'OK' || data === 'SSL_CONNECT_OK') resolve('success')
        else resolve(data)
        // 斷開連接
        ePosDevCheck.disconnect()
      }
    )
  })
}

/**
 * 列印明細單
 * @param {number} templateVersion 模板版本
 * @param {string} ipAddress 印表機 IP
 * @param {Object} printData 列印資料
 * @param {string} title 標題
 * @param {Object} options 其他設定
 * @param {Function} options.onReceive 印表機回傳列印結果時觸發，參數為 { success, code, status }（送出後才會觸發，不影響本函式的 resolve 時機）
 * @param {Object} options.receiptStyle V4 樣式參數，僅後門頁試調時傳入；正式流程不傳，使用 eposReceiptCanvas.js 的 DEFAULT_RECEIPT_STYLE
 * @param {number} options.copies 列印張數，僅模板 V4 支援，預設 1；同一連線內連續印出並各自裁切
 * @returns {Promise<void>} 送出列印後 resolve；連線失敗、建立裝置失敗或超過 PRINT_SETUP_TIMEOUT 時 reject（逾時為 'TIMEOUT'）
 */
export const printThermalPrinter = async (templateVersion, ipAddress, printData = {}, title = '明細單\n', { onReceive, receiptStyle, copies = 1 } = {}) => {
  /**
   * V4 以點陣圖列印，需在連線前先畫好明細單（載入 Logo 為非同步）
   * 各模板函式必須維持同步執行：多台出單機同時列印時共用模組層級的 printer 變數
   */
  const receiptCanvas = templateVersion === 4 ? await drawReceiptCanvas(printData, receiptStyle) : null
  printer = null
  return new Promise((resolve, reject) => {
    const ePosDev = new epson.ePOSDevice()
    console.log('printThermalPrinter', ePosDev, templateVersion, ipAddress, printData)

    let isSettled = false // 本次列印是否已有結果（送出、失敗或逾時）
    /**
     * 結束本次列印流程，確保 resolve / reject 只執行一次
     * @param {Function} fn resolve 或 reject
     * @param {*} value 回傳值
     */
    const settle = (fn, value) => {
      if (isSettled) return
      isSettled = true
      clearTimeout(setupTimer)
      fn(value)
    }
    /**
     * 釋放出單機連線：有裝置物件時先關閉裝置再斷線，否則直接斷線
     * SDK 的 disconnect 重複呼叫無副作用，因此不另做防重
     * @param {Object} device createDevice 取得的裝置物件
     */
    const releaseConnection = (device) => {
      const disconnect = () => {
        try {
          ePosDev.disconnect()
        } catch (error) {
          console.error('printThermalPrinter disconnect error', error)
        }
      }
      if (device) {
        ePosDev.deleteDevice(device, disconnect)
      } else {
        disconnect()
      }
    }

    // SDK 的 createDevice 沒有逾時機制，出單機無回應時 Promise 會永遠不結束，故在此統一設定上限
    const setupTimer = setTimeout(() => {
      console.error('printThermalPrinter timeout', ipAddress)
      settle(reject, 'TIMEOUT')
      releaseConnection()
    }, PRINT_SETUP_TIMEOUT)

    ePosDev.connect(ipAddress, port, (data) => {
      // 已逾時：不再列印，僅釋放連線
      if (isSettled) {
        releaseConnection()
        return
      }
      if (data === 'OK' || data === 'SSL_CONNECT_OK') {
        ePosDev.createDevice('local_printer', ePosDev.DEVICE_TYPE_PRINTER, { crypto: true, buffer: false }, (devobj, retcode) => {
          // 已逾時：不再列印，避免已回報失敗後才又印出單據
          if (isSettled) {
            releaseConnection(devobj)
            return
          }
          if (retcode === 'OK') {
            printer = devobj
            printer.timeout = PRINTER_TIMEOUT
            try {
              switch (templateVersion) {
                case 3:
                  printTemplateV3(printData)
                  break
                case 4:
                  printTemplateV4(receiptCanvas, copies)
                  break
              }
            } catch (error) {
              releaseConnection(devobj)
              settle(reject, error)
              return
            }
            // 送出後若遲遲收不到列印結果（例如連線中斷），時間到仍強制釋放連線
            const releaseTimer = setTimeout(() => releaseConnection(devobj), PRINTER_TIMEOUT + RELEASE_FALLBACK_DELAY)
            printer.onreceive = function (res) {
              console.log('Printer Object Created res', res)
              if (onReceive) onReceive(res)
              // 收到列印結果後釋放連線，避免連線累積佔用出單機
              clearTimeout(releaseTimer)
              releaseConnection(devobj)
            }
            printer.oncoveropen = function () { console.log('Printer Cover Open') }
            settle(resolve)
          } else {
            console.error('printThermalPrinter error retcode', retcode)
            releaseConnection()
            settle(reject, retcode)
          }
        })
      } else {
        releaseConnection()
        settle(reject, data)
      }
    })
  })
}

/**
 * 字型設定
 * 1. 排版時，「餐點品項名稱與數量、單價、金額」使用 FONT_A；而規格使用 FONT_B
 * 2. 當前的字型設定會決定一行可以放多少單位的內容(unitsPerLine)
 * 3. leftSideUnits 指得是左邊的單位數
 * [FONT_A]
 * 用於置放「餐點品項名稱與數量」，而「單價 金額」則是靠右對齊
 * 「單價 金額」目前給予的單位數是6，"50000  50000" (中間兩個空白) 共 12 個 0.5 單位 (12 * 0.5 = 6)
 * 而因為兩側中間要有間隔，所以左側空間給予 9 單位 (17.5 - 6 - 2 = 9.5)
 * [FONT_B]
 * 用於置放規格，為了大致與「餐點品項名稱與數量」對齊，所以給予 10.5 單位
 * 2025/04/08 註記(重要!!)：
 * 1. 因應使用者反饋字體太小，所以目前並沒有採用 FONT_B(而是 textSize = M)，而字元單位仍是採用 FONT_A 的設定
 */
const fontSetting = {
  fontA: { unitsPerLine: 17.5, leftSideUnits: 9.5, leftSidePaddingRight: 2, rightSideUnits: 6 },
  fontB: { unitsPerLine: 21, leftSideUnits: 10.5 }
}

/**
 * 印點數類型與交易前後點數
 * @param {*} pointType 點數類型
 * @param {*} before 交易前點數
 * @param {*} after 交易後點數
 */
const printPointLine = (pointType, before, after) => {
  // 將數字補足到5位數，不足的從前面補空白
  const paddedBefore = before.toString().padStart(5, ' ')
  const paddedAfter = after.toString().padStart(5, ' ')
  const rightSideContent = `${paddedBefore}${getContents(' ', 2)}${paddedAfter}`
  const rightSideRemainingUnits = fontSetting.fontA.rightSideUnits - getStringUnits(rightSideContent)
  /**
   * 計算填補的空白數
   * 左側剩餘: 9.5 - 4 = 5.5
   * 加上左側padding right: 5.5 + 2 = 7.5
   * 計算空白數量: 7.5 / 0.5 = 15
   * 然後再加上右側剩餘單位的空白數量: rightSideRemainingUnits / 0.5
   */
  const fillUnits = 15 + rightSideRemainingUnits / 0.5
  printer.addText(`${pointType}${getContents(' ', fillUnits)}${rightSideContent}\n`)
}

/**
 * 列印模板V3
 * 1. 與 V2 版差異如下：
 * (1) 新增訂單編號
 * (2) 新增用餐方式
 * (3) 移除服務員 / 新增備註
 * (4) 部分呈現樣式調整
 * 更新日期：2026/03/27
 * @param {object} data
 * @param {string | undefined} title
 */
const printTemplateV3 = (data) => {
  // 設定語言
  printer.addTextLang('zh-tw')
  // 設定字體大小
  printer.addTextSize(1, 2)

  printer.addTextAlign(printer.ALIGN_CENTER)
  printer.addText(`明細單${data.isVoidOrCancel ? '(作廢/取消)' : ''}\n`)
  printer.addText(`${getContents('-', fontSetting.fontA.unitsPerLine / 0.5)}\n`) // 加入分隔線

  printer.addFeedUnit(8) // 垂直間隔
  printer.addTextAlign(printer.ALIGN_LEFT)
  printer.addText(`訂單：${data.comOrderId}\n`) // 訂單編號
  printer.addFeedUnit(8) // 垂直間隔
  printer.addText(`${data.dining}\n`) // 用餐方式
  printer.addFeedUnit(8) // 垂直間隔
  printer.addText(`戶號：${data.userId}\n`) // 戶號

  printer.addFeedUnit(20) // 垂直間隔
  printer.addTextAlign(printer.ALIGN_LEFT)
  printer.addText(`品項/規格${getContents(' ', 17)}單價 金額\n`) // 餐點品項欄位標題
  printer.addFeedUnit(16) // 垂直間隔
  // 餐點品項列表
  for (let i = 0; i < data.items.length; i++) {
    // 目前餐點品項
    const currentItem = data.items[i]
    // 處理名稱與數量
    const originalItemTitle = `${currentItem.name}*${currentItem.quantity}`
    // 拆解餐點品項與數量
    const itemTitleSplit = getStringArray(originalItemTitle, fontSetting.fontA.leftSideUnits)
    /**
     * 先處理第一行的左右兩側內容
     * 1. 左側：餐點品項名稱與數量
     * 邏輯說明： 內容 + 補足左側剩餘可用單位數的空白 + 右側間隔空白
     * 2. 右側：單價 金額
     * 3. 左右兩側相加
     * 邏輯說明： 左側內容 + 補足右側剩餘可用單位數的空白 + 右側內容
     */
    const leftSideContent = `${itemTitleSplit[0]}${getContents(' ', (fontSetting.fontA.leftSideUnits - getStringUnits(itemTitleSplit[0])) / 0.5)}${getContents(' ', fontSetting.fontA.leftSidePaddingRight / 0.5)}`
    const rightSideContent = `${currentItem.price}${getContents(' ', 2)}${currentItem.quantity * currentItem.price}`
    printer.addText(`${leftSideContent}${getContents(' ', (fontSetting.fontA.rightSideUnits - rightSideContent.length * 0.5) / 0.5)}${rightSideContent}\n`)
    // 如果「餐點品項與數量」拆解結果超過一行，則接續打印
    if (itemTitleSplit.length > 1) {
      for (let i = 1; i < itemTitleSplit.length; i++) {
        printer.addText(`${itemTitleSplit[i]}\n`)
      }
    }
    // 處理規格
    printer.addFeedUnit(12) // 垂直間隔
    const itemSpecSplit = getStringArray(currentItem.displaySpec, fontSetting.fontA.leftSideUnits)
    for (let i = 0; i < itemSpecSplit.length; i++) {
      printer.addText(`${itemSpecSplit[i]}\n`)
    }
    printer.addText(`${getContents('.', fontSetting.fontA.unitsPerLine / 0.5)}\n`) // 加入分隔線
    printer.addFeedUnit(4) // 垂直間隔
  }
  printer.addTextAlign(printer.ALIGN_RIGHT)
  printer.addText(`合計：${data.totalPoint}\n`)
  printer.addFeedUnit(48) // 垂直間隔

  printer.addTextAlign(printer.ALIGN_LEFT)
  printer.addText(`點數類型${getContents(' ', 14)}交易前 交易後\n`)
  printer.addFeedUnit(36) // 垂直間隔
  if (data.isPointSupplement) {
    // 啟用點數補足：顯示兩種點數
    printPointLine('一般點數', data.normalPointBefore, data.normalPointAfter)
    printer.addFeedUnit(16) // 垂直間隔
    printPointLine('現金點數', data.cashPointBefore, data.cashPointAfter)
  } else {
    // 未啟用點數補足：根據選擇的點數類型顯示
    const pointType = data.selectedPointType === 1 ? '一般點數' : '現金點數'
    const before = data.selectedPointType === 1 ? data.normalPointBefore : data.cashPointBefore
    const after = data.selectedPointType === 1 ? data.normalPointAfter : data.cashPointAfter
    printPointLine(pointType, before, after)
  }

  printer.addFeedUnit(12) // 垂直間隔
  printer.addText(`${getContents('-', fontSetting.fontA.unitsPerLine / 0.5)}\n`) // 加入分隔線
  printer.addFeedUnit(24) // 垂直間隔
  printer.addText(`${data.isVoidOrCancel ? '作廢/取消備註' : '備註'}：${data.note}\n`)
  printer.addFeedUnit(24) // 垂直間隔
  printer.addText(`時間：${data.date}\n`)
  printer.addFeedUnit(24) // 垂直間隔
  printer.addText(`簽名：${getContents('_', 20)}\n`)

  printer.addCut(printer.CUT_FEED)
  printer.send()

  console.log('Print done!')
}

/**
 * 列印模板V4
 * 1. 與 V3 版差異如下：
 * (1) 整張明細單改由 canvas 繪製後以點陣圖列印（繪製邏輯見 eposReceiptCanvas.js）
 * (2) 新增智生活 Logo
 * (3) 標題列改為黑底白字
 * (4) 「單價」欄改為「數量」欄，並加上黑底方塊
 * (5) 規格前新增項目符號
 * (6) 「作廢/取消」文案改為「拒單/作廢/取消」
 * 更新日期：2026/09/24
 * @param {HTMLCanvasElement} canvas drawReceiptCanvas 產生的純黑白明細單
 * @param {number} copies 列印張數
 */
const printTemplateV4 = (canvas, copies = 1) => {
  const ctx = canvas.getContext('2d')
  // 張數防呆：至少 1 張、最多 6 張
  const printCopies = Math.min(6, Math.max(1, Math.trunc(copies) || 1))
  // canvas 已是純黑白，以門檻值模式原樣輸出
  printer.halftone = printer.HALFTONE_THRESHOLD
  printer.brightness = 1.0
  printer.addTextAlign(printer.ALIGN_LEFT)
  for (let i = 0; i < printCopies; i++) {
    // 分段送出，避免單一影像指令過大
    for (let y = 0; y < canvas.height; y += RASTER_BAND_HEIGHT) {
      const height = Math.min(RASTER_BAND_HEIGHT, canvas.height - y)
      printer.addImage(ctx, 0, y, canvas.width, height, printer.COLOR_1, printer.MODE_MONO)
    }
    printer.addCut(printer.CUT_FEED)
  }
  printer.send()

  console.log('Print done!')
}

/**
 * 生成一個包含指定內容與重複指定次數的字符串
 * @param {string} content - 要重複的內容。
 * @param {number} count - 重複內容的次數。
 * @returns {string} 包含重複內容的字符串。
 */
const getContents = (content, count) => {
  return Array(count).fill(content).join('')
}

/**
 * 取得字串陣列
 * @param {string} str 要拆解的字串
 * @param {number} unitsPerLine 每行的單位數
 * @returns {string[]} 拆解後的字串陣列
 */
const getStringArray = (str, unitsPerLine) => {
  const result = [] // 結果陣列
  let currentLine = '' // 目前行
  let currentLineUnits = 0 // 目前行的單位數

  for (let i = 0; i < str.length; i++) {
    const char = str[i] // 目前字元
    const charUnits = /[\u4e00-\u9fa5]/.test(char) ? 1 : 0.5 // 目前字元的單位數

    if (currentLineUnits + charUnits > unitsPerLine) {
      result.push(currentLine)
      currentLine = char
      currentLineUnits = charUnits
    } else {
      currentLine += char
      currentLineUnits += charUnits
    }
  }

  if (currentLine) {
    result.push(currentLine)
  }

  return result
}

/**
 * 取得字串單位數
 * @param {string} str 要計算的字串
 */
const getStringUnits = (str) => {
  return str.split('').reduce((acc, cur) => {
    return acc + (/[\u4e00-\u9fa5]/.test(cur) ? 1 : 0.5)
  }, 0)
}
